/**
 * Admin Console → Coupons → "New coupon" (gods only).
 *
 * Amount in USD (credited as prepaid tokens), scope — every workspace may
 * redeem it once, or one workspace (single-use) — plus optional cap on total
 * redemptions, expiry date, custom code and internal note.  Leaving the code
 * empty lets the backend generate a high-entropy one.  Validation mirrors the
 * backend bounds so a refused request is the exception, not the rule.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePickerInput, DropdownMenu, Input, InputTextArea, Modal, Radio, RadioGroup, SparkModalPortal, Text, sparkToast } from '@spark';
import { createCoupon } from '../api/adminApi';

export const NEW_COUPON_LABEL = 'New coupon';
export const CREATE_COUPON_LABEL = 'Create coupon';
export const SCOPE_ALL = 'all';
export const SCOPE_WORKSPACE = 'workspace';
export const SCOPE_ALL_LABEL = 'All workspaces (each may redeem once)';
export const SCOPE_WORKSPACE_LABEL = 'One workspace (single use)';

export const COUPON_MIN_AMOUNT_USD = 1;
export const COUPON_MAX_AMOUNT_USD = 100000;
export const COUPON_CODE_MIN_LENGTH = 6;
export const COUPON_CODE_MAX_LENGTH = 40;
export const COUPON_NOTE_MAX_LENGTH = 500;
/** Upper-case letters, digits and inner hyphens — the backend's `COUPON_CODE_PATTERN`. */
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]*[A-Z0-9]$/;

export const AMOUNT_ERROR = `Enter a whole-dollar amount between $${COUPON_MIN_AMOUNT_USD.toLocaleString('en-US')} and $${COUPON_MAX_AMOUNT_USD.toLocaleString('en-US')}.`;
export const WORKSPACE_REQUIRED_ERROR = 'Pick the workspace that may redeem this coupon.';
export const MAX_REDEMPTIONS_ERROR = 'Enter a whole number of 1 or more, or leave it empty for no cap.';
export const CODE_ERROR = `Use ${COUPON_CODE_MIN_LENGTH}-${COUPON_CODE_MAX_LENGTH} letters, digits or hyphens (no leading or trailing hyphen).`;
export const EXPIRY_ERROR = 'The expiry date must be in the future.';

const startOfToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** A coupon that "expires Nov 10" is redeemable through the end of that day. */
export const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const parseWholeNumber = (text) => {
    const trimmed = String(text ?? '').trim();
    if (!/^\d+$/.test(trimmed)) return null;
    return Number(trimmed);
};

/** Normalise what the god typed into the form the backend accepts (upper-case, no spaces). */
export const normalizeCodeInput = (value) => String(value || '').toUpperCase().replace(/\s+/g, '');

/** Validate the form; returns `{ errors, payload }` where `payload` is only set when there are no errors. */
export function validateCouponForm({ amountText, scope, workspaceId, maxRedemptionsText, code, expiresAt, note }) {
    const errors = {};
    const amountUsd = parseWholeNumber(amountText);
    if (amountUsd === null || amountUsd < COUPON_MIN_AMOUNT_USD || amountUsd > COUPON_MAX_AMOUNT_USD) {
        errors.amount = AMOUNT_ERROR;
    }
    if (scope === SCOPE_WORKSPACE && !workspaceId) errors.workspace = WORKSPACE_REQUIRED_ERROR;

    let maxRedemptions = null;
    if (scope === SCOPE_ALL && String(maxRedemptionsText ?? '').trim()) {
        maxRedemptions = parseWholeNumber(maxRedemptionsText);
        if (maxRedemptions === null || maxRedemptions < 1) errors.maxRedemptions = MAX_REDEMPTIONS_ERROR;
    }

    const normalizedCode = normalizeCodeInput(code);
    if (normalizedCode && (
        normalizedCode.length < COUPON_CODE_MIN_LENGTH
        || normalizedCode.length > COUPON_CODE_MAX_LENGTH
        || !CODE_PATTERN.test(normalizedCode)
    )) {
        errors.code = CODE_ERROR;
    }

    let expiry = null;
    if (expiresAt instanceof Date) {
        expiry = endOfDay(expiresAt);
        if (expiry.getTime() <= Date.now()) errors.expiresAt = EXPIRY_ERROR;
    }

    if (Object.keys(errors).length) return { errors, payload: null };
    return {
        errors,
        payload: {
            amountUsd,
            workspaceId: scope === SCOPE_WORKSPACE ? workspaceId : null,
            code: normalizedCode || null,
            maxRedemptions,
            expiresAt: expiry,
            note: String(note || '').trim() || null,
        },
    };
}

export default function CreateCouponModal({ open, workspaces, onClose, onCreated }) {
    const [amountText, setAmountText] = useState('');
    const [scope, setScope] = useState(SCOPE_ALL);
    const [workspaceId, setWorkspaceId] = useState(null);
    const [workspaceOpen, setWorkspaceOpen] = useState(false);
    const [maxRedemptionsText, setMaxRedemptionsText] = useState('');
    const [code, setCode] = useState('');
    const [expiresAt, setExpiresAt] = useState(null);
    const [note, setNote] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setAmountText('');
        setScope(SCOPE_ALL);
        setWorkspaceId(null);
        setWorkspaceOpen(false);
        setMaxRedemptionsText('');
        setCode('');
        setExpiresAt(null);
        setNote('');
        setErrors({});
        setSubmitting(false);
    }, [open]);

    const workspaceItems = useMemo(
        () => (workspaces || []).map((ws) => ({
            label: ws.name,
            description: ws.owner_email || undefined,
            onClick: () => {
                setWorkspaceId(ws.id);
                setWorkspaceOpen(false);
                setErrors((prev) => (prev.workspace ? { ...prev, workspace: undefined } : prev));
            },
        })),
        [workspaces],
    );
    const selectedWorkspace = useMemo(() => (workspaces || []).find((ws) => ws.id === workspaceId) || null, [workspaces, workspaceId]);

    const clearError = (key) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

    const close = () => {
        if (submitting) return;
        onClose();
    };

    const submit = async () => {
        if (submitting) return;
        const { errors: nextErrors, payload } = validateCouponForm({
            amountText, scope, workspaceId, maxRedemptionsText, code, expiresAt, note,
        });
        setErrors(nextErrors);
        if (!payload) return;
        setSubmitting(true);
        const result = await createCoupon(payload);
        setSubmitting(false);
        if (!result.ok) {
            // A taken custom code (409) and a rejected code/expiry (422) are the god's to fix inline.
            if (result.status === 409) setErrors({ code: result.error });
            else if (result.status === 422) sparkToast.error(result.error);
            else sparkToast.error(result.error);
            return;
        }
        const created = result.data?.coupon;
        sparkToast.success(created?.code ? `Coupon ${created.code} was created` : 'Coupon created');
        onCreated(created);
    };

    return (
        <SparkModalPortal open={open} onClose={close} width={560} destroyOnClose verticalAlign="top">
            <Modal
                open
                title={NEW_COUPON_LABEL}
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel={submitting ? 'Creating…' : CREATE_COUPON_LABEL}
                primaryDisabled={submitting}
                tertiaryLabel="Cancel"
                tertiaryDisabled={submitting}
                onPrimaryClick={submit}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <form
                    className="admin-form"
                    data-testid="create-coupon-form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <Input
                        label="Amount (USD)"
                        prefixText="$"
                        placeholder="0"
                        size="medium"
                        inputMode="numeric"
                        autoFocus
                        value={amountText}
                        danger={Boolean(errors.amount)}
                        helperText={errors.amount || 'Credited to the workspace balance as prepaid credits.'}
                        showHelperText
                        disabled={submitting}
                        onChange={(event) => {
                            setAmountText(event.target.value);
                            clearError('amount');
                        }}
                    />

                    <fieldset className="admin-form__fieldset">
                        <Text as="legend" variant="base-medium" color="primary">Who can redeem it</Text>
                        <RadioGroup
                            name="coupon-scope"
                            value={scope}
                            disabled={submitting}
                            className="admin-form__radios"
                            onChange={(next) => {
                                setScope(next);
                                clearError('workspace');
                                clearError('maxRedemptions');
                            }}
                        >
                            <Radio value={SCOPE_ALL} label={SCOPE_ALL_LABEL} />
                            <Radio value={SCOPE_WORKSPACE} label={SCOPE_WORKSPACE_LABEL} />
                        </RadioGroup>
                    </fieldset>

                    {scope === SCOPE_WORKSPACE ? (
                        <div className="admin-form__field">
                            <DropdownMenu
                                selectType="single-select"
                                label="Workspace"
                                showLabel
                                placeholder="Select a workspace"
                                size="medium"
                                fill
                                searchable
                                fuzzySearch
                                searchPlaceholder="Search workspaces"
                                searchEmptyText="No workspaces match"
                                disabled={submitting}
                                open={workspaceOpen}
                                onOpenChange={setWorkspaceOpen}
                                value={selectedWorkspace?.name}
                                text={selectedWorkspace?.name}
                                items={workspaceItems}
                            />
                            {errors.workspace ? (
                                <Text as="p" variant="detail-regular" className="admin-form__error">{errors.workspace}</Text>
                            ) : null}
                        </div>
                    ) : (
                        <Input
                            label="Maximum total redemptions (optional)"
                            placeholder="No cap"
                            size="medium"
                            inputMode="numeric"
                            value={maxRedemptionsText}
                            danger={Boolean(errors.maxRedemptions)}
                            helperText={errors.maxRedemptions || 'Stops the coupon after this many workspaces redeemed it.'}
                            showHelperText
                            disabled={submitting}
                            onChange={(event) => {
                                setMaxRedemptionsText(event.target.value);
                                clearError('maxRedemptions');
                            }}
                        />
                    )}

                    <div className="admin-form__field">
                        <div className="admin-form__row">
                            <DatePickerInput
                                label="Expiry date (optional)"
                                placeholder="Never expires"
                                value={expiresAt}
                                minDate={startOfToday()}
                                disabled={submitting}
                                onChange={(date) => {
                                    setExpiresAt(date);
                                    clearError('expiresAt');
                                }}
                            />
                            {expiresAt ? (
                                <Button variant="link" size="small" disabled={submitting} onClick={() => setExpiresAt(null)}>
                                    Clear
                                </Button>
                            ) : null}
                        </div>
                        {errors.expiresAt ? (
                            <Text as="p" variant="detail-regular" className="admin-form__error">{errors.expiresAt}</Text>
                        ) : null}
                    </div>

                    <Input
                        label="Code (optional)"
                        placeholder="Generated automatically"
                        size="medium"
                        value={code}
                        maxLength={COUPON_CODE_MAX_LENGTH}
                        danger={Boolean(errors.code)}
                        helperText={errors.code || 'Leave empty for a random high-entropy code.'}
                        showHelperText
                        disabled={submitting}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) => {
                            setCode(normalizeCodeInput(event.target.value));
                            clearError('code');
                        }}
                    />

                    <InputTextArea
                        label="Internal note (optional)"
                        placeholder="Why this coupon exists - only gods see it"
                        value={note}
                        size="small"
                        stretch
                        maxChars={COUPON_NOTE_MAX_LENGTH}
                        showCharCount
                        disabled={submitting}
                        onChange={(event) => setNote(event.target.value)}
                    />
                    <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
                </form>
            </Modal>
        </SparkModalPortal>
    );
}
